import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LibraryStudentCard from './components/LibraryStudentCard';
import LibraryApprovalCard from './components/LibraryApprovalCard';
import './LibraryPages.css';
import { getMeCached } from '../authCache';
import { fetchLibraryBoard, fetchLibraryPointEvents } from '../api/library';
import LibraryPointAnimations from './components/LibraryPointAnimations';
import useDanduClassLive from '../../live/useDanduClassLive';
import { fetchClasses } from '../stargarden/api/starGardenApi';
import {
  getGradeLevelFromClassName,
  getMarathonBadgePageThreshold,
  getPeriodStats,
  getStarPageUnit,
  getSuperBadgePageThreshold,
  getTopGrowthStudentId,
} from './utils/libraryScoring';

const periodOptions = [
  { key: 'week', label: 'Bu Hafta' },
  { key: 'month', label: 'Bu Ay' },
  { key: 'schoolYear', label: 'Bu Öğrenim Yılı' },
];

const normalizePeriodStats = (source) => {
  const normalized = {
    pages: Number(source?.pages ?? source?.pageCount ?? source?.page_count) || 0,
    books: Number(source?.books ?? source?.bookCount ?? source?.book_count) || 0,
  };

  const streakDaysRaw = source?.streakDays ?? source?.streak_days;
  if (streakDaysRaw !== undefined && streakDaysRaw !== null) {
    normalized.streakDays = Number(streakDaysRaw) || 0;
  }

  return normalized;
};

const pickFirstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const getFlatPeriodSource = ({ pages, books, streakDays }) => {
  if (pages === undefined && books === undefined && streakDays === undefined) return null;
  return { pages, books, streakDays };
};

const extractPreviousWeekSource = (item) => {
  return pickFirstDefined(
    item?.previousWeek,
    item?.prevWeek,
    item?.previous_week,
    item?.prev_week,
    item?.weekPrevious,
    item?.week_previous,
    item?.week?.previous,
    item?.week?.prev,
    item?.week?.previousPeriod,
    item?.week?.previous_period,
    getFlatPeriodSource({
      pages: item?.previousWeekPages ?? item?.previous_week_pages,
      books: item?.previousWeekBooks ?? item?.previous_week_books,
      streakDays: item?.previousWeekStreakDays ?? item?.previous_week_streak_days,
    }),
  );
};

const extractPreviousMonthSource = (item) => {
  return pickFirstDefined(
    item?.previousMonth,
    item?.prevMonth,
    item?.previous_month,
    item?.prev_month,
    item?.monthPrevious,
    item?.month_previous,
    item?.month?.previous,
    item?.month?.prev,
    item?.month?.previousPeriod,
    item?.month?.previous_period,
    getFlatPeriodSource({
      pages: item?.previousMonthPages ?? item?.previous_month_pages,
      books: item?.previousMonthBooks ?? item?.previous_month_books,
      streakDays: item?.previousMonthStreakDays ?? item?.previous_month_streak_days,
    }),
  );
};

const normalizeStudents = (items) => {
  return (items || []).map((item) => {
    const previousWeekSource = extractPreviousWeekSource(item);
    const previousMonthSource = extractPreviousMonthSource(item);

    return {
      id: String(item?.id ?? ''),
      firstName: String(item?.firstName ?? item?.first_name ?? ''),
      avatar_id: item?.avatar_id ?? null,
      starter_emoji: item?.starter_emoji ?? null,
      week: normalizePeriodStats(item?.week),
      month: normalizePeriodStats(item?.month),
      schoolYear: normalizePeriodStats(item?.schoolYear),
      ...(previousWeekSource ? { previousWeek: normalizePeriodStats(previousWeekSource) } : {}),
      ...(previousMonthSource ? { previousMonth: normalizePeriodStats(previousMonthSource) } : {}),
    };
  });
};

const LibraryScreenPage = () => {
  const navigate = useNavigate();
  const [periodKey, setPeriodKey] = useState('week');
  const [helpOpen, setHelpOpen] = useState(false);

  const [schoolId, setSchoolId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const loadPointEvents = useCallback((after) => fetchLibraryPointEvents(schoolId, selectedClass?.id, after), [schoolId, selectedClass?.id]);
  const classLiveConnected = useDanduClassLive({
    enabled: Boolean(schoolId && selectedClass?.id),
    path: schoolId && selectedClass?.id
      ? `/admin/mini-tasks/${encodeURIComponent(schoolId)}/classes/${encodeURIComponent(selectedClass.id)}/live/events?mode=reward`
      : null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError('');
        const me = await getMeCached();
        if (!me?.ok || !me?.data?.admin_user?.school_id) {
          throw new Error('Okul bilgisi bulunamadı.');
        }
        if (cancelled) return;
        setSchoolId(me.data.admin_user.school_id);
      } catch (e) {
        if (!cancelled) {
          setStudents([]);
          setError(e?.message || 'Kütüphane verileri yüklenemedi.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const list = await fetchClasses(schoolId);
        if (cancelled) return;

        const sorted = (list || []).sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'tr-TR'),
        );
        setClasses(sorted);

        if (sorted.length === 0) {
          setError('Bu okula ait sınıf bulunamadı.');
          setLoading(false);
          return;
        }

        if (sorted.length === 1) {
          setSelectedClass(sorted[0]);
          return;
        }

        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setStudents([]);
          setError(e?.message || 'Sınıflar yüklenemedi.');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId || !selectedClass?.name) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const board = await fetchLibraryBoard(schoolId, { className: selectedClass.name });
        if (cancelled) return;
        setStudents(normalizeStudents(board?.students || []));
      } catch (e) {
        if (!cancelled) {
          setStudents([]);
          setError(e?.message || 'Kütüphane verileri yüklenemedi.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [schoolId, selectedClass]);

  const sortedStudents = useMemo(() => {
    return [...students].sort((a, b) => {
      const aStats = getPeriodStats(a, periodKey);
      const bStats = getPeriodStats(b, periodKey);
      if (bStats.pages !== aStats.pages) return bStats.pages - aStats.pages;
      if (bStats.books !== aStats.books) return bStats.books - aStats.books;
      return (a.firstName || '').localeCompare(b.firstName || '', 'tr-TR');
    });
  }, [periodKey, students]);

  const topGrowthStudentId = useMemo(
    () => getTopGrowthStudentId(students, periodKey),
    [periodKey, students],
  );

  const totals = useMemo(() => {
    return sortedStudents.reduce(
      (acc, student) => {
        const stats = getPeriodStats(student, periodKey);
        acc.pages += stats.pages;
        acc.books += stats.books;
        if (stats.pages > 0) acc.activeReaders += 1;
        return acc;
      },
      { pages: 0, books: 0, activeReaders: 0 },
    );
  }, [periodKey, sortedStudents]);

  const gradeLevel = useMemo(
    () => getGradeLevelFromClassName(selectedClass?.name),
    [selectedClass?.name],
  );
  const starPageUnit = useMemo(() => getStarPageUnit(gradeLevel), [gradeLevel]);
  const superBadgePageThreshold = useMemo(() => getSuperBadgePageThreshold(gradeLevel), [gradeLevel]);
  const marathonBadgePageThreshold = useMemo(() => getMarathonBadgePageThreshold(gradeLevel), [gradeLevel]);

  if (!loading && schoolId && classes.length > 1 && !selectedClass) {
    return <Box className="library-page"><Box className="library-page__content">
      <Paper className="library-header" component="header">
        <Box className="library-header__identity">
          <IconButton className="library-header__back" aria-label="Panele dön" onClick={() => navigate('/admin')}><ArrowBackIcon /></IconButton>
          <Box className="library-header__book" aria-hidden="true">📚</Box>
          <Typography component="h1">Kütüphanem</Typography>
        </Box>
      </Paper>
      <LibraryApprovalCard schoolId={schoolId} />
      <Paper sx={{ p: 3, borderRadius: '22px' }}>
        <Typography component="h2" sx={{ fontSize: 20, fontWeight: 700, mb: 2 }}>Sınıfınızı seçin</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {classes.map((cls) => <Button className="library-primary" key={cls.id} variant="contained" onClick={() => setSelectedClass(cls)} sx={{ minWidth: 100 }}>{cls.name}</Button>)}
        </Box>
      </Paper>
    </Box></Box>;
  }

  if (loading) {
    return <Box className="library-page" sx={{ minHeight: 240, display: 'grid', placeContent: 'center', justifyItems: 'center', gap: 2 }}>
      <CircularProgress />
      <Typography color="text.secondary">Kütüphane yükleniyor...</Typography>
    </Box>;
  }

  return (
    <Box className="library-page">
      {schoolId && selectedClass?.id && <LibraryPointAnimations loadEvents={loadPointEvents} classLive classLiveConnected={classLiveConnected} onAwards={() => {
        fetchLibraryBoard(schoolId, { className: selectedClass.name }).then((board) => setStudents(normalizeStudents(board.students || []))).catch(() => {});
      }} />}
      <Box component="main" className="library-page__content">
        <Paper className="library-header" component="header">
          <Box className="library-header__identity">
            <Tooltip title="Panele dön">
              <IconButton className="library-header__back" aria-label="Panele dön" onClick={() => navigate('/admin')}><ArrowBackIcon /></IconButton>
            </Tooltip>
            <Box className="library-header__book" aria-hidden="true">📚</Box>
            <Typography component="h1">Kütüphanem</Typography>
            <IconButton className="library-header__help" aria-label="Yardım" onClick={() => setHelpOpen(true)}><HelpOutlineIcon /></IconButton>
            {selectedClass && <Box component="span" sx={{ bgcolor: '#e1f8ec', color: '#169665', borderRadius: '999px', px: 1.5, py: 0.5, fontWeight: 750, fontSize: 13 }}>📚 {selectedClass.name}</Box>}
          </Box>
          <Box className="library-header__actions">
            {classes.length > 1 && <Button className="library-class-change" variant="outlined" size="small" onClick={() => { setSelectedClass(null); setStudents([]); }}>Sınıf Değiştir</Button>}
            <Tabs className="library-periods" value={periodKey} onChange={(_, value) => setPeriodKey(value)} textColor="inherit">
              {periodOptions.map((period) => <Tab key={period.key} value={period.key} label={period.label} />)}
            </Tabs>
          </Box>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        <LibraryApprovalCard schoolId={schoolId} />

        <Grid className="library-stats" container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <Paper elevation={2} sx={{ borderRadius: 4, p: { xs: 1.5, md: 2 }, bgcolor: 'rgba(255,255,255,0.8)' }}>
              <Typography sx={{ color: 'text.secondary', fontWeight: 700, fontSize: 'clamp(14px, 1.1vw, 18px)' }}>Toplam Sayfa</Typography>
              <Typography sx={{ fontWeight: 900, fontSize: 'clamp(28px, 2.6vw, 46px)', lineHeight: 1.1 }}>{totals.pages}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper elevation={2} sx={{ borderRadius: 4, p: { xs: 1.5, md: 2 }, bgcolor: 'rgba(255,255,255,0.8)' }}>
              <Typography sx={{ color: 'text.secondary', fontWeight: 700, fontSize: 'clamp(14px, 1.1vw, 18px)' }}>Bitirilen Kitap</Typography>
              <Typography sx={{ fontWeight: 900, fontSize: 'clamp(28px, 2.6vw, 46px)', lineHeight: 1.1 }}>{totals.books}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper elevation={2} sx={{ borderRadius: 4, p: { xs: 1.5, md: 2 }, bgcolor: 'rgba(255,255,255,0.8)' }}>
              <Typography sx={{ color: 'text.secondary', fontWeight: 700, fontSize: 'clamp(14px, 1.1vw, 18px)' }}>Aktif Okur</Typography>
              <Typography sx={{ fontWeight: 900, fontSize: 'clamp(28px, 2.6vw, 46px)', lineHeight: 1.1 }}>{totals.activeReaders}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Grid container rowSpacing={{ xs: 2.6, md: 3.2 }} columnSpacing={{ xs: 2.6, md: 3.2 }}>
          {sortedStudents.map((student) => (
            <Grid key={student.id} item xs={12} sm={6} md={4} lg={4} xl={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ width: '100%', maxWidth: 560 }}>
                <LibraryStudentCard
                  student={student}
                  periodKey={periodKey}
                  gradeLevel={gradeLevel}
                  isTopGrowth={topGrowthStudentId === student.id}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            bgcolor: 'rgba(255,255,255,0.98)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Kütüphanem Nasıl Çalışır?</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.5 }}>⭐ Yıldız Sistemi</Typography>
          <Typography sx={{ fontSize: 15, mb: 0.5 }}>• Her {starPageUnit} sayfa = 1 yıldız</Typography>
          <Typography sx={{ fontSize: 15, mb: 0.5 }}>• Kartta en fazla 10 yıldız görünür, fazlası +N olarak yazılır</Typography>
          <Typography sx={{ fontSize: 15, mb: 1.2 }}>• Kartta sonraki yıldıza kalan sayfa da gösterilir</Typography>
          <Typography sx={{ fontSize: 15, mb: 0.5 }}>• Her 10 yıldızda 1 adet 🏅 rozet kazanılır (isim yanında xN görünür)</Typography>
          <Typography sx={{ fontSize: 15, mb: 1.2 }}>• Bu hafta yeni rozet kazanan öğrenci kartı altın çerçeve ve ✨ etiketle vurgulanır</Typography>

          <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.5 }}>🌱 Seviyeler</Typography>
          <Typography sx={{ fontSize: 15, mb: 0.5 }}>• 🌱 Okuma Yolunda: 0-3 yıldız</Typography>
          <Typography sx={{ fontSize: 15, mb: 0.5 }}>• 📘 Okur: 4-6 yıldız</Typography>
          <Typography sx={{ fontSize: 15, mb: 0.5 }}>• 📚 Kitap Dostu: 7-9 yıldız</Typography>
          <Typography sx={{ fontSize: 15, mb: 1.2 }}>• 🏆 Kitap Kurdu: 10+ yıldız</Typography>

          <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.5 }}>🔥 Alışkanlık Rozetleri</Typography>
          <Typography sx={{ fontSize: 15, mb: 0.5 }}>• 🔥 Seri x3: 7+ gün</Typography>
          <Typography sx={{ fontSize: 15, mb: 0.5 }}>• 📘 Düzenli x2: 5-6 gün</Typography>
          <Typography sx={{ fontSize: 15, mb: 1.2 }}>• 📘 Düzenli x1: 3-4 gün</Typography>

          <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.5 }}>📚 Kitap Rozetleri</Typography>
          <Typography sx={{ fontSize: 15, mb: 0.5 }}>• 📚 Dost x3: 5+ kitap</Typography>
          <Typography sx={{ fontSize: 15, mb: 0.5 }}>• 📖 Bitirici x2: 3-4 kitap</Typography>
          <Typography sx={{ fontSize: 15, mb: 1.2 }}>• 📖 Bitirici x1: 2 kitap</Typography>

          <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.5 }}>🌟 Özel Rozetler</Typography>
          <Typography sx={{ fontSize: 15, mb: 0.5 }}>• 📈 Gelişen x1: Bu Hafta/Bu Ay sekmesinde, önceki aynı döneme göre sayfa farkı en yüksek olan</Typography>
          <Typography sx={{ fontSize: 15, mb: 0.5 }}>• 🚀 Süper x1: Bu Öğrenim Yılı sekmesinde {superBadgePageThreshold}+ sayfa</Typography>
          <Typography sx={{ fontSize: 15, mb: 1.2 }}>• 🏔️ Maraton x1: Bu Ay sekmesinde {marathonBadgePageThreshold}+ sayfa</Typography>

          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
            Not: Tüm hesaplar seçili sekmeye göre yapılır (Bu Hafta / Bu Ay / Bu Öğrenim Yılı).
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LibraryScreenPage;
